import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { Plus } from 'lucide-react';
import { UNIT_ANALYSIS_VIBES } from '../../constants/unitAnalysisVibes';

const GOLDEN_ANGLE = Math.PI * (3 - Math.sqrt(5));
const CLOUD_FRAME = 'relative mt-8 mb-6 h-[36rem] w-full max-w-full sm:h-[40rem] lg:h-[25rem]';

interface PlacedVibe {
  phrase: string;
  x: number;
  y: number;
  size: number;
  tilt: number;
  duration: number;
  delay: number;
}

function hashPhrase(phrase: string): number {
  let hash = 0;
  for (const char of phrase) hash = (hash * 33 + char.charCodeAt(0)) % 997;
  return hash;
}

const PLUS_SPOTS = [
  { id: 'nw', x: 22, y: 14, delay: 0.15 },
  { id: 'ne', x: 78, y: 18, delay: 0.7 },
  { id: 'sw', x: 16, y: 82, delay: 1.05 },
  { id: 'se', x: 74, y: 78, delay: 0.4 },
];

function plusObstacles(boxW: number, boxH: number) {
  return PLUS_SPOTS.map((spot) => ({
    cx: (spot.x / 100) * boxW,
    cy: (spot.y / 100) * boxH,
    width: Math.min(140, boxW * 0.18),
    height: Math.min(110, boxH * 0.16),
  }));
}

function layoutVibes(phrases: string[], boxW: number, boxH: number, avoidPluses = true): PlacedVibe[] {
  const count = phrases.length;
  const square = boxW / Math.max(boxH, 1) < 1.45;
  const fontScale = square ? Math.min(1, Math.max(0.55, boxW / 560)) : 1;
  const gapX = 22 * fontScale;
  const gapY = 16 * fontScale;
  const obstacles = plusObstacles(boxW, boxH);
  const nodes = phrases.map((phrase, index) => {
    const hash = hashPhrase(phrase);
    const size = (0.92 + (hash % 6) * 0.1) * fontScale;
    const fontPx = size * 16;
    const width = Math.max(64, phrase.length * fontPx * 0.66);
    const height = fontPx * 2.15;
    const spread = Math.sqrt(index / Math.max(count - 1, 1));
    const angle = index * GOLDEN_ANGLE;
    const rawX = Math.cos(angle);
    const rawY = Math.sin(angle);
    const squareNorm = Math.max(Math.abs(rawX), Math.abs(rawY), 0.001);
    const placeX = square ? rawX / squareNorm : rawX;
    const placeY = square ? rawY / squareNorm : rawY;
    const reach = 0.22 + 0.72 * spread;
    return {
      phrase,
      size,
      tilt: (hash % 9) - 4,
      duration: 4.2 + (hash % 18) / 10,
      delay: 0,
      width,
      height,
      cx: boxW / 2 + placeX * (square ? boxW * 0.46 * reach : 28 + boxW * 0.4 * spread),
      cy: boxH / 2 + placeY * (square ? boxH * 0.46 * reach : 16 + boxH * 0.32 * spread),
    };
  });

  for (let pass = 0; pass < 180; pass += 1) {
    for (let i = 0; i < nodes.length; i += 1) {
      for (let j = i + 1; j < nodes.length; j += 1) {
        const a = nodes[i];
        const b = nodes[j];
        const dx = b.cx - a.cx;
        const dy = b.cy - a.cy;
        const overlapX = (a.width + b.width) / 2 + gapX - Math.abs(dx);
        const overlapY = (a.height + b.height) / 2 + gapY - Math.abs(dy);
        if (overlapX <= 0 || overlapY <= 0) continue;
        const dirX = dx === 0 ? (i % 2 === 0 ? 1 : -1) : Math.sign(dx);
        const dirY = dy === 0 ? 1 : Math.sign(dy);
        const edgeRoom = (node: (typeof nodes)[number], axis: 'x' | 'y', direction: number) => {
          const center = axis === 'x' ? node.cx : node.cy;
          const limit = axis === 'x' ? boxW : boxH;
          const inset = (axis === 'x' ? node.width : node.height) / 2 + 2;
          return direction > 0 ? limit - inset - center : center - inset;
        };
        const spaceX = Math.max(0, edgeRoom(a, 'x', -dirX)) + Math.max(0, edgeRoom(b, 'x', dirX));
        const spaceY = Math.max(0, edgeRoom(a, 'y', -dirY)) + Math.max(0, edgeRoom(b, 'y', dirY));
        const canX = spaceX >= overlapX;
        const canY = spaceY >= overlapY;
        if ((overlapX <= overlapY && canX) || (canX && !canY)) {
          const move = overlapX * 0.5;
          a.cx -= dirX * move;
          b.cx += dirX * move;
        } else if (canY) {
          const move = overlapY * 0.5;
          a.cy -= dirY * move;
          b.cy += dirY * move;
        } else {
          a.cx += Math.sign(boxW / 2 - a.cx) * 8;
          a.cy += Math.sign(boxH / 2 - a.cy) * 8;
          b.cx += Math.sign(boxW / 2 - b.cx) * 8;
          b.cy += Math.sign(boxH / 2 - b.cy) * 8;
        }
      }
    }
    for (const node of nodes) {
      if (!avoidPluses) break;
      for (const obstacle of obstacles) {
        const dx = node.cx - obstacle.cx;
        const dy = node.cy - obstacle.cy;
        const overlapX = (node.width + obstacle.width) / 2 - Math.abs(dx);
        const overlapY = (node.height + obstacle.height) / 2 - Math.abs(dy);
        if (overlapX <= 0 || overlapY <= 0) continue;
        const towardCenterX = boxW / 2 - node.cx;
        const towardCenterY = boxH / 2 - node.cy;
        if (overlapX < overlapY) {
          const dir = towardCenterX === 0 ? (dx >= 0 ? 1 : -1) : Math.sign(towardCenterX);
          node.cx += dir * (overlapX + 4);
        } else {
          const dir = towardCenterY === 0 ? (dy >= 0 ? 1 : -1) : Math.sign(towardCenterY);
          node.cy += dir * (overlapY + 4);
        }
      }
    }
    for (const node of nodes) {
      const padX = node.width / 2 + 2;
      const padY = node.height / 2 + 2;
      node.cx = Math.min(boxW - padX, Math.max(padX, node.cx));
      node.cy = Math.min(boxH - padY, Math.max(padY, node.cy));
    }
  }

  for (let pass = 0; pass < 60; pass += 1) {
    for (let i = 0; i < nodes.length; i += 1) {
      for (let j = i + 1; j < nodes.length; j += 1) {
        const a = nodes[i];
        const b = nodes[j];
        const dx = b.cx - a.cx;
        const dy = b.cy - a.cy;
        const overlapX = (a.width + b.width) / 2 + gapX - Math.abs(dx);
        const overlapY = (a.height + b.height) / 2 + gapY - Math.abs(dy);
        if (overlapX <= 0 || overlapY <= 0) continue;
        const hitsPlus = (node: (typeof nodes)[number]) =>
          avoidPluses &&
          obstacles.some((obstacle) => {
            const hitX = (node.width + obstacle.width) / 2 - Math.abs(node.cx - obstacle.cx);
            const hitY = (node.height + obstacle.height) / 2 - Math.abs(node.cy - obstacle.cy);
            return hitX > 0 && hitY > 0;
          });
        const apply = (horizontal: boolean) => {
          const before = [a.cx, a.cy, b.cx, b.cy];
          if (horizontal) {
            const dir = dx === 0 ? 1 : Math.sign(dx);
            a.cx -= dir * overlapX * 0.5;
            b.cx += dir * overlapX * 0.5;
          } else {
            const dir = dy === 0 ? 1 : Math.sign(dy);
            a.cy -= dir * overlapY * 0.5;
            b.cy += dir * overlapY * 0.5;
          }
          if (hitsPlus(a) || hitsPlus(b)) {
            a.cx = before[0];
            a.cy = before[1];
            b.cx = before[2];
            b.cy = before[3];
            return false;
          }
          return true;
        };
        if (overlapX < overlapY) {
          if (!apply(true)) apply(false);
        } else if (!apply(false)) apply(true);
      }
    }
    for (const node of nodes) {
      const padX = node.width / 2 + 2;
      const padY = node.height / 2 + 2;
      node.cx = Math.min(boxW - padX, Math.max(padX, node.cx));
      node.cy = Math.min(boxH - padY, Math.max(padY, node.cy));
    }
  }

  return nodes.map((node) => ({
    phrase: node.phrase,
    x: (node.cx / boxW) * 100,
    y: (node.cy / boxH) * 100,
    size: node.size,
    tilt: node.tilt,
    duration: node.duration,
    delay: node.delay,
  }));
}

function VibePlusButton({
  x,
  y,
  delay,
  onAdd,
}: {
  x: number;
  y: number;
  delay: number;
  onAdd: (phrase: string) => void;
}): ReactElement {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');

  const submit = () => {
    const phrase = value.trim().replace(/\s+/g, ' ');
    if (!phrase) return;
    onAdd(phrase);
    setValue('');
    setOpen(false);
  };

  return (
    <div
      className={open ? 'absolute z-30' : 'vibe-float absolute z-20'}
      style={{
        left: `${x}%`,
        top: `${y}%`,
        animationDelay: `${delay}s`,
        transform: open ? 'translate(-50%, -50%)' : undefined,
      }}
    >
      {open ? (
        <form
          className="flex items-center gap-1 rounded-full bg-white dark:bg-gray-800 py-1 pl-3 pr-1 shadow-md ring-1 ring-gray-200"
          onSubmit={(event) => {
            event.preventDefault();
            submit();
          }}
        >
          <input
            autoFocus
            value={value}
            maxLength={40}
            onChange={(event) => setValue(event.target.value)}
            placeholder="A word"
            aria-label="Word to add"
            className="w-28 bg-transparent py-1 text-sm text-gray-900 dark:text-white outline-none"
          />
          <button type="submit" className="rounded-full bg-gray-900 px-2.5 py-1 text-xs font-medium text-white">
            Add
          </button>
        </form>
      ) : (
        <button
          type="button"
          aria-label="Add a word"
          onClick={() => setOpen(true)}
          className="flex h-7 w-7 items-center justify-center rounded-full border border-gray-200 dark:border-white/10 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 shadow-sm hover:border-gray-400 hover:text-gray-900 dark:hover:text-white"
        >
          <Plus className="h-3.5 w-3.5" strokeWidth={2.5} />
        </button>
      )}
    </div>
  );
}

function useCloudBox(): { ref: (node: HTMLDivElement | null) => void; width: number; height: number } {
  const ref = useRef<HTMLDivElement | null>(null);
  const [size, setSize] = useState({ width: 980, height: 400 });

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const measure = () => {
      const rect = node.getBoundingClientRect();
      const width = Math.max(1, Math.round(rect.width));
      const height = Math.max(1, Math.round(rect.height));
      setSize((current) => (current.width === width && current.height === height ? current : { width, height }));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return {
    ref: (node) => {
      ref.current = node;
    },
    width: size.width,
    height: size.height,
  };
}

function groupWordClass(count: number): string {
  if (count >= 4) return 'bg-rose-600 font-semibold text-white shadow-md';
  if (count === 3) return 'bg-violet-600 font-semibold text-white shadow-md';
  if (count === 2) return 'bg-sky-600 font-semibold text-white shadow-sm';
  return 'font-medium text-gray-500 dark:text-gray-400';
}

export function GroupVibeCloud({
  phrases,
  emptyLabel = 'Words your group chose show up here.',
}: {
  phrases: { phrase: string; count: number }[];
  emptyLabel?: string;
}): ReactElement {
  const cloud = useCloudBox();
  const phraseKey = phrases.map((phrase) => `${phrase.count}:${phrase.phrase}`).join('\n');
  const words = useMemo(() => {
    const labels = phraseKey === '' ? [] : phraseKey.split('\n').map((entry) => entry.slice(entry.indexOf(':') + 1));
    return layoutVibes(labels, cloud.width, cloud.height, false);
  }, [cloud.height, cloud.width, phraseKey]);
  const counts = new Map(phrases.map((phrase) => [phrase.phrase, phrase.count]));

  return (
    <div ref={cloud.ref} className={CLOUD_FRAME}>
      {words.length === 0 ? (
        <p className="absolute inset-0 flex items-center justify-center px-8 text-center text-sm text-gray-400 dark:text-gray-500">
          {emptyLabel}
        </p>
      ) : null}
      {words.map((word) => {
        const count = counts.get(word.phrase) ?? 1;
        const people = count === 1 ? '1 person' : `${count} people`;
        return (
          <span
            key={word.phrase}
            title={`${word.phrase}, ${people}`}
            className="vibe-float absolute z-10"
            style={{
              left: `${word.x}%`,
              top: `${word.y}%`,
              animationDuration: `${word.duration}s`,
              animationDelay: `${word.delay}s`,
            }}
          >
            <span className="inline-block" style={{ transform: `rotate(${word.tilt}deg)` }}>
              <span
                className={`inline-block origin-center whitespace-nowrap rounded-full px-2 py-0.5 ${groupWordClass(count)}`}
                style={{ fontSize: `${word.size}rem` }}
              >
                {word.phrase}
              </span>
            </span>
          </span>
        );
      })}
    </div>
  );
}

export default function VibeWordCloud({
  customPhrases,
  isSelected,
  onToggle,
  onAdd,
}: {
  customPhrases: string[];
  isSelected: (phrase: string) => boolean;
  onToggle: (phrase: string) => void;
  onAdd: (phrase: string) => void;
}): ReactElement {
  const cloud = useCloudBox();
  const extraKey = customPhrases.join('\n');
  const words = useMemo(
    () => layoutVibes([...UNIT_ANALYSIS_VIBES, ...extraKey.split('\n').filter(Boolean)], cloud.width, cloud.height),
    [cloud.height, cloud.width, extraKey],
  );

  return (
    <div ref={cloud.ref} className={CLOUD_FRAME}>
      {words.map((word) => {
        const selected = isSelected(word.phrase);
        return (
          <button
            key={word.phrase}
            type="button"
            aria-pressed={selected}
            onClick={() => onToggle(word.phrase)}
            className="vibe-float absolute z-10 hover:z-20"
            style={{
              left: `${word.x}%`,
              top: `${word.y}%`,
              animationDuration: `${word.duration}s`,
              animationDelay: `${word.delay}s`,
            }}
          >
            <span className="inline-block" style={{ transform: `rotate(${word.tilt}deg)` }}>
              <span
                className={`inline-block origin-center whitespace-nowrap rounded-full px-2 py-0.5 transition duration-200 ease-out hover:scale-125 ${
                  selected
                    ? 'scale-110 bg-gray-900 font-semibold text-white shadow-md'
                    : 'font-medium text-gray-500 hover:text-gray-900'
                }`}
                style={{ fontSize: `${word.size}rem` }}
              >
                {word.phrase}
              </span>
            </span>
          </button>
        );
      })}
      {PLUS_SPOTS.map((spot) => (
        <VibePlusButton key={spot.id} x={spot.x} y={spot.y} delay={spot.delay} onAdd={onAdd} />
      ))}
    </div>
  );
}
